import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import SpeakingLogo from '../ui/SpeakingLogo';
import eventBus from '../../utils/eventBus';
import { useGlobalAudio } from '../../context/GlobalAudioContext';
import { useTutorial } from '../../context/TutorialContext';
import { getStoredUser } from '../../context/AuthContext';
import { getNudgeForEvent, dismissNudge } from '../../services/helpService';

const FILES_BASE_URL = import.meta.env.VITE_FILES_BASE_URL || 'https://www.cabaza.be/files';

function parseJson(str) {
    if (!str) return {};
    if (typeof str === 'object') return str;
    try { return JSON.parse(str); } catch { return {}; }
}

/**
 * NudgeManager — écoute l'eventBus et affiche les coups de pouce configurés.
 * Monté au root de l'app.
 */
const NudgeManager = () => {
    const { isPlaying, playAudio, stopAudio } = useGlobalAudio();
    const { activeTutorial, startTutorial } = useTutorial();
    const navigate = useNavigate();
    const [currentNudge, setCurrentNudge] = useState(null);
    const lang = 'fr';

    const handleEvent = useCallback(async (eventName) => {
        if (activeTutorial) return; // pas de nudge pendant un tutoriel
        if (currentNudge) return;   // un seul nudge à la fois
        try {
            const user = getStoredUser();
            const userUuid = user?.peopleUuid || '';
            const nudge = await getNudgeForEvent(eventName, userUuid, lang);
            if (!nudge || !nudge.uuid) return;

            const delay = nudge.displaydelayms ?? 800;
            setTimeout(() => {
                const msg = parseJson(nudge.messagebylang)[lang] || '';
                const audio = parseJson(nudge.audiobylang)[lang];
                const cta = parseJson(nudge.ctaaction);
                setCurrentNudge({
                    uuid: nudge.uuid,
                    message: msg,
                    audioPath: audio,
                    ctaLabel: nudge.ctalabel || '',
                    ctaAction: cta,
                    userUuid
                });
                if (audio) {
                    playAudio(`${FILES_BASE_URL}/${audio}`, { anchor: 'nudge' });
                }
            }, delay);
        } catch (err) {
            console.warn('[NudgeManager] getNudgeForEvent error:', err?.message || err);
        }
    }, [activeTutorial, currentNudge, playAudio]);

    // S'abonner à tous les événements connus (wildcards non supportés par eventBus simple)
    useEffect(() => {
        const events = [
            'library.book.created',
            'library.book.listed',
            'contacts.person.added',
            'places.place.saved',
            'events.event.created',
            'advertisements.ad.published',
            'moments.moment.posted',
            'profile.completed',
            'onboarding.first-login'
        ];
        const unsubs = events.map(ev => eventBus.on(ev, () => handleEvent(ev)));
        return () => unsubs.forEach(u => u());
    }, [handleEvent]);

    const handleClose = async (actionTaken = false) => {
        if (!currentNudge) return;
        stopAudio();
        try {
            if (currentNudge.userUuid) {
                await dismissNudge(currentNudge.uuid, currentNudge.userUuid, actionTaken);
            }
        } catch (err) {
            console.warn('[NudgeManager] dismissNudge error', err?.message || err);
        }
        setCurrentNudge(null);
    };

    const handleCta = async () => {
        if (!currentNudge || !currentNudge.ctaAction) return;
        const action = currentNudge.ctaAction;
        await handleClose(true);
        if (action.type === 'navigate' && action.target) {
            navigate(action.target);
        } else if (action.type === 'start-tutorial' && action.tutorialUuid) {
            startTutorial(action.tutorialUuid);
        } else if (action.type === 'open-faq') {
            // TODO : déclencher l'ouverture du FaqPanel sur la FAQ précise
            navigate('/');
        }
    };

    if (!currentNudge) return null;

    return (
        <div
            style={{
                position: 'fixed',
                bottom: 24,
                right: 24,
                zIndex: 99997,
                maxWidth: 380,
                backgroundColor: '#8B5CF6',
                color: 'white',
                padding: 14,
                borderRadius: 16,
                boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
                display: 'flex',
                gap: 12,
                alignItems: 'flex-start',
                animation: 'nudgeSlideIn 0.3s ease-out'
            }}
        >
            <style>{`@keyframes nudgeSlideIn { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }`}</style>
            <SpeakingLogo size={52} isSpeaking={isPlaying} />
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, lineHeight: 1.4, marginBottom: 10 }}>
                    {currentNudge.message}
                </div>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center' }}>
                    <button
                        onClick={() => handleClose(false)}
                        style={{
                            background: 'rgba(255,255,255,0.15)',
                            color: 'white',
                            border: 'none',
                            borderRadius: 8,
                            padding: '6px 10px',
                            cursor: 'pointer',
                            fontSize: 12
                        }}
                    >
                        Plus tard
                    </button>
                    {currentNudge.ctaLabel && currentNudge.ctaAction && currentNudge.ctaAction.type && (
                        <button
                            onClick={handleCta}
                            style={{
                                background: 'white',
                                color: '#8B5CF6',
                                border: 'none',
                                borderRadius: 8,
                                padding: '6px 12px',
                                cursor: 'pointer',
                                fontSize: 12,
                                fontWeight: 600
                            }}
                        >
                            {currentNudge.ctaLabel}
                        </button>
                    )}
                </div>
            </div>
            <button
                onClick={() => handleClose(false)}
                style={{
                    background: 'transparent',
                    color: 'white',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 2,
                    opacity: 0.7
                }}
                aria-label="Fermer"
            >
                <X size={16} />
            </button>
        </div>
    );
};

export default NudgeManager;
